import { z } from 'zod';
import { EventCheckoutSchema, FormFieldSchema, PublicEventStatusSchema } from './config.ts';
import { OrganizationPublicSchema } from './organization.ts';

/** Response schemas for the public read endpoints — docs/02_OPENAPI.yaml. */

export const HealthResponseSchema = z.strictObject({
  status: z.literal('ok'),
  version: z.string(),
});

export const PublicEventSchema = z.strictObject({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: PublicEventStatusSchema,
  timezone: z.string(),
  starts_at: z.iso.datetime({ offset: true }).nullable().optional(),
  ends_at: z.iso.datetime({ offset: true }).nullable().optional(),
  config_version: z.int().min(1),
  branding: z.record(z.string(), z.unknown()).optional(),
  event_url: z.url().nullable().optional(),
});

export const PublicLocationSchema = z.strictObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  province: z.string().nullable().optional(),
  aliases: z.array(z.string()),
  sort_order: z.int(),
  /**
   * True for the row that only exists to satisfy the foreign key. The client
   * auto-selects it and renders no location control at all.
   */
  is_placeholder: z.boolean(),
});

/**
 * The bootstrap envelope, and the one schema in this repo that tolerates an
 * unknown key: `z.object` ignores keys it does not know, every nested object
 * below stays `strictObject`.
 *
 * This is the opposite of the rule `admin-write.ts` follows, and deliberately
 * so — the two schemas sit on opposite sides of a boundary:
 *
 * - Config an admin **writes** is strict because a typo like `buttonLabel`
 *   must fail while they are still looking at the form. Ignored, it publishes
 *   cleanly and renders nothing, and nobody finds out until event day.
 * - A response an old client **reads** must tolerate keys added after that
 *   client shipped. The client treats a parse failure as `invalid_response`
 *   and renders the offline screen, whose retry button can never succeed
 *   because the response is not going to change. The service worker keeps the
 *   old bundle until the tab is fully closed, so the participant is stuck on a
 *   screen only a full tab close clears — and nobody knows to do that.
 *
 * `checkout` is what made it concrete: the moment an organizer enables
 * check-out and publishes, every device still on a cached bundle would parse
 * the new key as a contract violation. That is true of every additive field
 * this response will ever gain, so the envelope, not the field, is the fix.
 *
 * Strict where a human can be told; tolerant where a stale binary cannot.
 *
 * Only the outermost envelope loosens. A malformed *known* field still fails
 * (a half-parsed form would let someone submit against a location list the
 * server never sent), and an unknown key inside `event` or a location still
 * fails, because those two shapes are the contract itself.
 *
 * A form field is the one exception, and it predates this envelope:
 * `FormFieldSchema` is `z.looseObject`, mirroring `additionalProperties: true`
 * in the OpenAPI contract so a source-controlled config file carrying an extra
 * key does not fail a build. An unknown key inside a field is ignored, not
 * rejected — check `config.ts` before assuming otherwise.
 */
export const EventBootstrapResponseSchema = z.object({
  event: PublicEventSchema,
  locations: z.array(PublicLocationSchema),
  form: z.strictObject({
    fields: z.array(FormFieldSchema),
  }),
  messages: z.record(z.string(), z.unknown()).optional(),
  organization: OrganizationPublicSchema.nullable().optional(),
  /**
   * Present only while the event has check-out on. The key's absence is the
   * client's whole test, so an event that switched check-out off is
   * indistinguishable from one that never had it.
   */
  checkout: EventCheckoutSchema.optional(),
});

export type HealthResponse = z.infer<typeof HealthResponseSchema>;
export type PublicEvent = z.infer<typeof PublicEventSchema>;
export type PublicLocation = z.infer<typeof PublicLocationSchema>;
export type EventBootstrapResponse = z.infer<typeof EventBootstrapResponseSchema>;
